import React from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/custom/ui/card";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/custom/ui/tabs";
import { Gift, Lock } from "lucide-react";
import { Icon } from "@iconify/react";
import { ScrollArea } from "@/components/ui/custom/ui/scroll-area";

const EndOfSeasonRewards = ({ seasonInfo }) => {
  return (
    <Card className="bg-[#1a1a1a] border border-zinc-800 mt-12 w-full px-2 sm:px-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white text-xl sm:text-2xl">
          {/* <Gift className="h-8 w-8" />  */}
          <Icon icon="mdi:present" className="h-6 w-6" />
          End of Season Rewards
        </CardTitle>
        <CardDescription className="text-zinc-400 text-sm sm:text-md text-left">
          Qualifying users will receive token airdrops based on hidden criteria
        </CardDescription>
      </CardHeader>
      <CardContent className="w-full">
        <Tabs defaultValue="overview" className="w-full">
          <ScrollArea className="">
            <TabsList className="flex h-auto p-1 bg-zinc-700 rounded-lg w-fit text-left items-start justify-start min-w-[320px] shadow-sm gap-2">
              <TabsTrigger
                value="overview"
                className="data-[state=active]:bg-zinc-600 data-[state=active]:text-white data-[state=active]:shadow-sm text-zinc-300 bg-transparent rounded-md px-4 py-2 text-sm sm:text-base font-medium transition-all hover:text-white hover:bg-zinc-650 min-w-[110px]"
              >
                Overview
              </TabsTrigger>
              <TabsTrigger
                value="criteria"
                className="data-[state=active]:bg-zinc-600 data-[state=active]:text-white data-[state=active]:shadow-sm text-zinc-300 bg-transparent rounded-md px-4 py-2 text-sm sm:text-base font-medium transition-all hover:text-white hover:bg-zinc-650 min-w-[110px]"
              >
                Criteria
              </TabsTrigger>
              <TabsTrigger
                value="timeline"
                className="data-[state=active]:bg-zinc-600 data-[state=active]:text-white data-[state=active]:shadow-sm text-zinc-300 bg-transparent rounded-md px-4 py-2 text-sm sm:text-base font-medium transition-all hover:text-white hover:bg-zinc-650 min-w-[110px]"
              >
                Timeline
              </TabsTrigger>
            </TabsList>
          </ScrollArea>

          <TabsContent value="overview" className="mt-6 space-y-6 text-left">
            <div className="text-zinc-400 leading-relaxed text-sm sm:text-md">
              At the end of Season {seasonInfo?.number}, qualifying users will
              receive an airdrop of tokens based on their performance and
              activity throughout the season.
            </div>

            <div className="space-y-4">
              <h3 className="text-lg sm:text-xl font-semibold text-white">
                Reward Distribution
              </h3>
              <ul className="space-y-3 text-zinc-400 text-sm sm:text-md">
                <li className="flex items-start gap-2 w-full">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>
                    Users meeting all hidden criteria will be eligible for
                    airdrop
                  </span>
                </li>
                <li className="flex items-start gap-2 w-full">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>
                    Reward amounts will vary based on individual performance
                    metrics
                  </span>
                </li>
                <li className="flex items-start gap-2 w-full">
                  <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span>
                    Maintain consistent activity throughout the season
                  </span>
                </li>
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="criteria" className="mt-6 space-y-6 text-left">
            <div className="text-zinc-400 leading-relaxed text-sm sm:text-md">
              The exact criteria for qualifying for the airdrop are hidden until
              the end of the season. However, users are encouraged to:
            </div>

            <ul className="space-y-3 text-zinc-400 ml-4 text-sm sm:text-md">
              <li className="flex items-start gap-2 w-full">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>Complete as many missions as possible</span>
              </li>
              <li className="flex items-start gap-2 w-full">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>Acquire and hold XSEED tokens</span>
              </li>
              <li className="flex items-start gap-2 w-full">
                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></div>
                <span>Purchase full or fractional nodes</span>
              </li>
            </ul>

            <div className="bg-orange-100 border border-orange-200 p-3 sm:p-4 rounded-lg">
              <div className="flex items-start gap-3">
                <Lock className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm sm:text-base text-orange-800">
                    <span className="font-semibold">Note:</span> The hidden
                    criteria are designed to reward genuine engagement and
                    participation rather than last-minute point accumulation.
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="timeline" className="mt-6 text-left">
            <div className="space-y-4 text-zinc-400 text-sm sm:text-md">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 p-3 sm:p-4 bg-zinc-700 rounded-lg w-full">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <div>
                  <p className="font-medium">
                    Season {seasonInfo?.number} Ends
                  </p>
                  <p className="text-xs sm:text-base text-zinc-400">
                    {seasonInfo?.endDate} ({seasonInfo?.daysLeft} days
                    remaining)
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 p-3 sm:p-4 bg-zinc-700 rounded-lg opacity-60 w-full">
                <div className="w-3 h-3 bg-zinc-500 rounded-full"></div>
                <div>
                  <p className="font-medium">Criteria Revealed</p>
                  <p className="text-xs sm:text-base text-zinc-400">
                    Within 2 weeks of season end
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 p-3 sm:p-4 bg-zinc-700 rounded-lg opacity-60 w-full">
                <div className="w-3 h-3 bg-zinc-500 rounded-full"></div>
                <div>
                  <p className="font-medium">Airdrop Distribution</p>
                  <p className="text-xs sm:text-base text-zinc-400">
                    Within 2 weeks of season end
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default EndOfSeasonRewards;
